import Interval from '@interfaces/Interval';
import BlockType from '@data/schedules/types/BlockType';
import { checkIsBlockReleased } from '@components/SchedulePage/utilities/checkIsBlockReleased/checkIsBlockReleased';
import { getDateFromApiDateTime } from '@calendar/utilities';
import SlotResponse from '@data/schedules/types/SlotResponse';
import { BlockReleaseState } from '@data/blockReleases/types/BlockReleaseState';

export type ReleaseDetails = {
	releasedTimeFrames: Interval[];
	nonReleasedTimeFrames: Interval[];
	surgeonTimeFrames: Interval[];
	groupTimeFrames: Interval[];
	specialtyTimeFrames: Interval[];
	isReleased: boolean;
};

export function extractBlockReleaseDetails(blocks: Omit<SlotResponse, 'owners'>[]): ReleaseDetails {
	const hasSurgeonBlock = blocks.some((block) => block.type === BlockType.SURGEON);

	return blocks.reduce<ReleaseDetails>(
		(acc, block) => {
			const interval = {
				start: getDateFromApiDateTime(block.start),
				end: getDateFromApiDateTime(block.end),
			};

			// If the surgeon has any single surgeon blocks
			const isBlockReleased = hasSurgeonBlock
				? !isNonReleasedSurgeonBlock(block) // Then only consider the surgeon block for isReleased status
				: checkIsBlockReleased(block); // Otherwise, use standard check to determine if the block is released

			if (isBlockReleased) {
				acc.releasedTimeFrames.push(interval);
			} else {
				acc.nonReleasedTimeFrames.push(interval);
				if (block.type === BlockType.SURGEON) acc.surgeonTimeFrames.push(interval);
				if (block.type === BlockType.GROUP) acc.groupTimeFrames.push(interval);
				if (block.type === BlockType.SPECIALTY) acc.specialtyTimeFrames.push(interval);
			}

			acc.isReleased = acc.isReleased && isBlockReleased;

			return acc;
		},
		{
			releasedTimeFrames: [],
			nonReleasedTimeFrames: [],
			surgeonTimeFrames: [],
			groupTimeFrames: [],
			specialtyTimeFrames: [],
			isReleased: true,
		},
	);
}

export const isNonReleasedSurgeonBlock = (block: Omit<SlotResponse, 'owners'>): boolean => {
	return !checkIsBlockReleased(block) && block.type === BlockType.SURGEON;
};

const checkHasBlocksWithSameId = (
	block: Omit<SlotResponse, 'owners'>,
	allBlocks?: Omit<SlotResponse, 'owners'>[],
) => {
	return allBlocks && allBlocks.filter((b) => b.id === block.id).length > 1;
};

type IntervalWithId = { id: string; } & Interval;
export const combineTimeFrames = (
	timeFrames: Interval[],
	blocks: Omit<SlotResponse, 'owners'>[],
): Interval[] => {
	// Return original timeframes if conditions are not met
	if (timeFrames.length <= 1 || blocks.length < 1) return timeFrames;

	// Separate blocks into those with the same ID and those with different IDs
	const [blocksWithSameId, blocksWithDifferentId] = blocks.reduce<[IntervalWithId[], IntervalWithId[]]>(
		(acc, block) => {
			const interval = {
				id: block.id,
				start: getDateFromApiDateTime(block.start),
				end: getDateFromApiDateTime(block.end),
			};

			// Check if the block has the same ID as others and categorize accordingly
			acc[checkHasBlocksWithSameId(block, blocks) ? 0 : 1].push(interval);
			return acc;
		},
		[[], []],
	);

	// Combine blocks with the same ID and consecutive timeframes
	const combinedBlocks = blocksWithSameId.reduce<IntervalWithId[]>((acc, curr) => {
		const lastInterval = acc[acc.length - 1];

		// If the last interval ends when the current one starts, merge them
		if (lastInterval && lastInterval.end.getTime() === curr.start.getTime()) {
			lastInterval.end = curr.end;
		} else {
			// Otherwise, add the current interval to the accumulator
			acc.push({ ...curr });
		}
		return acc;
	}, []);

	// Only return concatenated blocks if combinedBlocks exist
	if (combinedBlocks.length > 0) {
		timeFrames = blocksWithDifferentId.concat(combinedBlocks);
	}

	// If there are multiple blocks with same ID and one of them is pending release, should combine with non-pending timeframes to display original block time
	// This logic should not apply for flip room scenario (different IDs)
	if (
		blocks.some((block) => {
			return block.release?.state === BlockReleaseState.PENDING_RELEASE &&
				blocksWithSameId[0]?.id === block.id;
		})
	) {
		timeFrames = timeFrames.reduce(
			(acc, timeframe) => {
				if (timeframe.end > acc[0].end) {
					acc[0].end = timeframe.end;
				}
				if (timeframe.start < acc[0].start) {
					acc[0].start = timeframe.start;
				}
				return acc;
			},
			[timeFrames[0]],
		);
	}

	return timeFrames;
};
